import { getBackendUrl } from './backend';
import {
  hasCurrentApplicationServerKey,
  isPushSyncDue,
  urlBase64ToUint8Array,
} from './push-encoding';
import { getServiceWorkerRegistration } from './service-worker';

type PushSubscriptionPayload = PushSubscriptionJSON & {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
};

const PUSH_SYNCED_AT_KEY = 'push-subscription-synced-at';
const SUBSCRIPTION_CLEAR_RETRIES = 5;
const SUBSCRIPTION_RETRY_DELAY_MS = 300;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export function isStandalonePwa(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isPushSupported(): boolean {
  return 'Notification' in window && 'PushManager' in window && 'serviceWorker' in navigator;
}

async function getVapidPublicKey(): Promise<string> {
  const response = await fetch(`${getBackendUrl()}/api/push/vapid-public-key`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('No se pudo cargar la configuración de notificaciones');
  }

  const data = await response.json();
  if (!data.publicKey) {
    throw new Error('Las notificaciones push no están configuradas');
  }
  return data.publicKey;
}

function subscriptionUsesCurrentKey(subscription: PushSubscription, publicKey: string): boolean {
  return hasCurrentApplicationServerKey(subscription.options.applicationServerKey, publicKey);
}

function shouldSyncExistingSubscription(): boolean {
  const stored = localStorage.getItem(PUSH_SYNCED_AT_KEY);
  const lastSyncedAt = stored ? Number(stored) : null;
  return isPushSyncDue(Number.isFinite(lastSyncedAt) ? lastSyncedAt : null, Date.now());
}

async function saveSubscription(subscription: PushSubscription): Promise<void> {
  const payload = subscription.toJSON() as PushSubscriptionPayload;
  await fetch(`${getBackendUrl()}/api/push/subscriptions`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  }).then((response) => {
    if (!response.ok) {
      throw new Error('No se pudo guardar la suscripción de notificaciones');
    }
    localStorage.setItem(PUSH_SYNCED_AT_KEY, String(Date.now()));
  });
}

async function waitForSubscriptionToClear(
  registration: ServiceWorkerRegistration,
): Promise<void> {
  for (let attempt = 0; attempt < SUBSCRIPTION_CLEAR_RETRIES; attempt += 1) {
    if (!(await registration.pushManager.getSubscription())) {
      return;
    }
    await delay(SUBSCRIPTION_RETRY_DELAY_MS);
  }
}

async function subscribeWithRetry(
  registration: ServiceWorkerRegistration,
  publicKey: string,
): Promise<PushSubscription> {
  try {
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  } catch (error) {
    await delay(SUBSCRIPTION_RETRY_DELAY_MS);

    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      return existing;
    }

    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
}

export async function unsubscribeFromPushNotifications(): Promise<void> {
  if (!isPushSupported()) {
    return;
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    localStorage.removeItem(PUSH_SYNCED_AT_KEY);
    return;
  }

  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) {
    localStorage.removeItem(PUSH_SYNCED_AT_KEY);
    return;
  }

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await waitForSubscriptionToClear(registration);
  localStorage.removeItem(PUSH_SYNCED_AT_KEY);

  const response = await fetch(`${getBackendUrl()}/api/push/subscriptions`, {
    method: 'DELETE',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ endpoint }),
  });

  if (!response.ok) {
    throw new Error('No se pudo desactivar la suscripción de notificaciones');
  }
}

export async function subscribeToPushNotifications(): Promise<PushSubscription> {
  if (!isPushSupported()) {
    throw new Error('Este navegador no soporta notificaciones push');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Permiso de notificaciones denegado');
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    throw new Error('No se pudo registrar el service worker');
  }

  const existing = await registration.pushManager.getSubscription();
  const publicKey = await getVapidPublicKey();
  if (existing && !subscriptionUsesCurrentKey(existing, publicKey)) {
    await existing.unsubscribe();
    await waitForSubscriptionToClear(registration);
  }

  const current = await registration.pushManager.getSubscription();
  const subscription = current ?? (await subscribeWithRetry(registration, publicKey));

  await saveSubscription(subscription);
  return subscription;
}

export async function syncExistingPushSubscription(): Promise<boolean> {
  if (!isPushSupported() || Notification.permission !== 'granted' || !shouldSyncExistingSubscription()) {
    if (!isPushSupported() || Notification.permission !== 'granted') {
      return false;
    }

    const registration = await getServiceWorkerRegistration();
    return !!(await registration?.pushManager.getSubscription());
  }

  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    return false;
  }

  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) {
    return false;
  }

  const publicKey = await getVapidPublicKey();
  if (!subscriptionUsesCurrentKey(subscription, publicKey)) {
    await subscription.unsubscribe();
    await waitForSubscriptionToClear(registration);
    await subscribeToPushNotifications();
    return true;
  }

  await saveSubscription(subscription);
  return true;
}
